import React, { useEffect, useRef, useState } from 'react';
import SearchableSetorSelect from '../Escalas Utils/SearchableSetorSelect';

const UF_OPTIONS = [
	'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT',
	'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO',
	'RR', 'SC', 'SP', 'SE', 'TO',
];

const WEEKDAY_LABELS = {
	1: 'Segunda', 2: 'Terça', 3: 'Quarta', 4: 'Quinta',
	5: 'Sexta', 6: 'Sábado', 7: 'Domingo',
};

const getTodayIsoDate = () => {
	const today = new Date();
	return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const normalizeTime = (value = '') => {
	const matched = String(value || '').match(/^(\d{1,2}):(\d{1,2})$/);
	if (!matched) return '';
	const hours = Math.min(23, Math.max(0, Number(matched[1])));
	const minutes = Math.min(59, Math.max(0, Number(matched[2])));
	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const normalizeHour = (value = '') => {
	const digits = String(value || '').replace(/\D/g, '').slice(0, 2);
	if (!digits) return '';
	const hours = Math.min(23, Number(digits));
	return `${String(hours).padStart(2, '0')}:00`;
};

const TimeField = ({ ariaLabel, disabled, value, onChange, compact }) => {
	const normalizedValue = normalizeTime(value);
	const displayValue = normalizedValue.slice(0, 2);
	const [draft, setDraft] = useState(displayValue);
	const isEditingRef = useRef(false);

	useEffect(() => {
		if (!isEditingRef.current) setDraft(displayValue);
	}, [displayValue]);

	const handleChange = (nextValue) => {
		setDraft(String(nextValue || '').replace(/\D/g, '').slice(0, 2));
	};

	const finishEditing = () => {
		isEditingRef.current = false;
		const completeTime = normalizeHour(draft);

		if (completeTime) {
			setDraft(completeTime.slice(0, 2));
			onChange?.(completeTime);
			return;
		}

		setDraft(displayValue);
	};

	return (
		<input
			type='text'
			aria-label={ariaLabel}
			placeholder='00'
			value={disabled ? '00' : draft}
			disabled={disabled}
			inputMode='numeric'
			maxLength={2}
			onFocus={() => {
				isEditingRef.current = true;
			}}
			onChange={(event) => handleChange(event.target.value)}
			onBlur={finishEditing}
			className={`${compact ? 'w-[20px] text-[10px]' : 'w-[19px] text-[13px]'} min-w-0 bg-transparent text-center font-normal outline-none ${disabled ? 'cursor-not-allowed text-gray-400' : 'text-[#4B5563]'}`}
		/>
	);
};

export const HourRangeControl = ({
	disabled = false,
	hasError = false,
	compact = false,
	minWidthClassName = 'min-w-[116px]',
	marginTopClassName = 'mt-2',
	containerClassName = '',
	horaInicio = '',
	horaFim = '',
	onChangeHoraInicio,
	onChangeHoraFim,
}) => (
	<div className={`${marginTopClassName} ${compact ? 'h-[22px]' : 'h-[34px]'} ${minWidthClassName} flex items-center ${compact ? 'justify-start gap-0.5 px-1' : 'justify-center gap-1 px-1'} rounded-md border ${disabled ? 'cursor-not-allowed border-[#F1F1F1] bg-[#fcfcfc]' : hasError ? 'border-[#D92D20] bg-white ring-1 ring-[#FECACA]' : 'border-gray-200 bg-white'} ${containerClassName}`}>
		<TimeField ariaLabel='Hora inicial' disabled={disabled} value={horaInicio} onChange={onChangeHoraInicio} compact={compact} />
		<span className={compact ? 'text-[9px]' : 'text-[13px]'} style={{ color: disabled ? '#9CA3AF' : '#6B7280' }}>às</span>
		<TimeField ariaLabel='Hora final' disabled={disabled} value={horaFim} onChange={onChangeHoraFim} compact={compact} />
	</div>
);

function NovoProfissionalMultiprofissionalModal({
	professional,
	setProfessional,
	isAllSectors,
	sectors,
	categories,
	vinculoTypes,
	regimes,
	recurrenceOptions,
	selectedRecurrence,
	weekDays,
	isEditing,
	editingScope,
	submitting,
	error,
	fieldErrors = {},
	onClearFieldError,
	onClose,
	onSearchCpf,
	onSubmit,
	formatCpf,
	formatPhone,
	onToggleWeekDay,
	onUpdateWeekDayTime,
	onToggleRotationWeekDay,
	onAddSpecificDate,
	onUpdateSpecificDate,
	onRemoveSpecificDate,
	onSectorSelected,
}) {
	const visibleRecurrences = [
		{ value: 'SEMANAL_12H', label: '12H (segunda à sexta)' },
		{ value: 'CICLO_12X60', label: 'Plantão 12×60' },
		{ value: 'SEMANAL', label: 'Semanal' },
		{ value: 'CICLO_24X120', label: 'Plantão 24×120' },
	];
	const updateSpecificHours = (index, field, value) => {
		onClearFieldError?.('horarios');
		setProfessional((current) => ({ ...current,
			datasEspecificasHorarios: current.datasEspecificas.map((_, dateIndex) => ({
				horaInicio: current.horaInicio, horaFim: current.horaFim,
				...current.datasEspecificasHorarios?.[dateIndex],
				...(index === dateIndex ? { [field]: value } : {}),
			})),
		}));
	};
	const hasSchedule = Boolean(professional.regime && professional.recorrencia && professional.vigenciaInicio);
	const isReadOnly = editingScope === 'visualizacao';
	const showSchedule = isReadOnly || !isEditing || !isAllSectors;
	const isScheduleReadOnly = isReadOnly;
	const isWeekly = selectedRecurrence?.tipo_recorrencia === 'SEMANAL';
	const isRotation = selectedRecurrence?.tipo_recorrencia === 'ROTACAO_PERIODICA';
	const isSpecificDates = selectedRecurrence?.tipo_recorrencia === 'DATAS_ESPECIFICAS';
	const fieldBorder = (field) => fieldErrors[field] ? 'border-[#D92D20] ring-1 ring-[#FECACA]' : 'border-gray-200';
	useEffect(() => {
		const selectedCategory = categories.find(
			(item) => String(item.value) === String(professional.categoria),
		);
		if (!selectedCategory?.label) return;
		setProfessional((current) =>
			current.cargo === selectedCategory.label
				? current
				: { ...current, cargo: selectedCategory.label },
		);
	}, [categories, professional.categoria, setProfessional]);
	const setField = (field, value) => {
		const nextValue =
			field === 'vigenciaInicio' && !isEditing && value < getTodayIsoDate()
				? getTodayIsoDate()
				: value;
		onClearFieldError?.(field);
		if (field === 'horaInicio' || field === 'horaFim') onClearFieldError?.('horarios');
		if (field === 'recorrencia') onClearFieldError?.('diasSemana', 'datasEspecificas', 'horarios');
		setProfessional((current) => {
			if (field === 'categoria') {
				const selectedCategory = categories.find(
					(item) => String(item.value) === String(nextValue),
				);
				return {
					...current,
					categoria: nextValue,
					cargo: selectedCategory?.label || '',
				};
			}
			return { ...current, [field]: nextValue };
		});
		if (field === 'setor') onSectorSelected?.(nextValue);
		if (field === 'recorrencia' && nextValue === 'SEMANAL_12H') {
			setProfessional((current) => ({ ...current, diasSemana: [1, 2, 3, 4, 5], horaInicio: '07:00', horaFim: '19:00', diasSemanaHorarios: {} }));
		}
	};
	const toggleWeekDay = (day) => {
		onClearFieldError?.('diasSemana');
		onClearFieldError?.('horarios');
		onToggleWeekDay(day);
	};
	const updateWeekDayTime = (day, field, value) => {
		onClearFieldError?.('horarios');
		onUpdateWeekDayTime(day, field, value);
	};
	const addSpecificDate = () => {
		onClearFieldError?.('datasEspecificas', 'horarios');
		onAddSpecificDate();
	};

	return (
		<div className='fixed inset-x-0 bottom-0 top-20 z-50 flex items-center justify-center overflow-hidden px-4 py-4 max-sm:px-2' role='dialog' aria-modal='true' aria-label={isEditing ? 'Editar profissional multiprofissional' : 'Adicionar novo profissional multiprofissional'}>
			<button type='button' className='absolute inset-0 bg-black/30' onClick={onClose} aria-label='Fechar modal' />
			<form onSubmit={onSubmit} className='relative max-h-[calc(100vh-7rem)] w-[92%] max-w-[940px] overflow-hidden rounded-md border border-gray-200 bg-white shadow-xl max-lg:w-full sm:max-lg:max-w-[620px] max-sm:max-w-[350px]'>
				<div className='flex h-[25px] w-full justify-end rounded-t-md bg-[#fcfcfc] px-1'>
					<button type='button' onClick={onClose} aria-label='Fechar' className='flex items-center pb-2 text-3xl text-gray-400 transition-all duration-300 hover:text-gray-600'>&times;</button>
				</div>

				<div className='max-h-[calc(100vh-9rem)] overflow-x-hidden overflow-y-auto px-4 py-5 font-inter max-sm:px-3 max-sm:py-3'>
					<fieldset disabled={isReadOnly} className='min-w-0 border-0 p-0 disabled:opacity-100'>
					<div className='mb-4 flex items-center justify-center gap-3 max-lg:flex-col max-lg:items-stretch max-lg:gap-2 max-sm:mb-3'>
						<span className='text-[14px] font-semibold text-[#13335A]'>CPF</span>
						<input type='text' placeholder='000.000.000-00' value={professional.cpf} onChange={(event) => setField('cpf', formatCpf(event.target.value))} inputMode='numeric' maxLength={14} readOnly={isEditing} className={`h-[32px] w-[210px] rounded-md border px-2 text-[13px] text-gray-600 outline-none max-lg:w-full ${isEditing ? 'cursor-not-allowed border-gray-200 bg-[#F3F4F6]' : `${fieldBorder('cpf')} bg-white`}`} />
						<button type='button' onClick={onSearchCpf} disabled={isEditing || submitting} className='h-[28px] rounded-md border border-gray-200 px-4 text-[13px] text-[#76797e] shadow-md transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'>Pesquisar</button>
					</div>
					<hr className='mb-4 border-gray-200 max-sm:mb-3' />

					{((isAllSectors && !isEditing) || isReadOnly) && <div className={`mb-4 rounded-md border bg-[#F8FAFC] p-3 ${fieldErrors.setor ? 'border-[#D92D20] ring-1 ring-[#FECACA]' : 'border-[#D8DEE8]'}`}><label className='mb-1 block text-[13px] font-medium text-[#13335A]'>Setor</label><SearchableSetorSelect value={professional.setor} options={sectors} onChange={(value) => setField('setor', value)} placeholder='Selecione o setor' emptyMessage='Nenhum setor encontrado' menuPortal className={`h-[34px] rounded-md border bg-white px-3 text-[13px] text-gray-600 outline-none ${fieldBorder('setor')}`} /></div>}

					{isReadOnly && <div role='status' className='mb-4 rounded-md border border-[#D7B46A] bg-[#FFF9E8] px-3 py-2 text-[12px] font-medium text-[#755719]'>Profissional com histórico mantido nesta competência, sem cards futuros. Os dados estão disponíveis somente para visualização.</div>}

					<div className='grid grid-cols-1 gap-x-3 gap-y-4 lg:grid-cols-2 max-sm:gap-y-3'>
						<label className='lg:col-span-2'><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Nome</span><input type='text' placeholder='Nome completo' value={professional.nome} onChange={(event) => setField('nome', event.target.value)} className={`h-[34px] w-full rounded-md border px-3 text-[13px] text-gray-600 outline-none ${fieldBorder('nome')}`} /></label>
						<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Admissão</span><input type='date' value={professional.admissao} onChange={(event) => setField('admissao', event.target.value)} className={`h-[34px] w-full rounded-md border bg-white px-3 text-[13px] text-gray-600 outline-none ${fieldBorder('admissao')}`} /></label>
						<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Vínculo</span><select value={professional.vinculoTipo} onChange={(event) => setField('vinculoTipo', event.target.value)} className={`h-[34px] w-full rounded-md border bg-white px-3 text-[13px] text-gray-600 outline-none ${fieldBorder('vinculoTipo')}`}><option value=''>Selecione o vínculo</option>{vinculoTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
						<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Cargo</span><input type='text' placeholder='Selecione a categoria' value={professional.cargo} readOnly aria-readonly='true' className='h-[34px] w-full cursor-not-allowed rounded-md border border-gray-200 bg-[#F3F4F6] px-3 text-[13px] text-gray-600 outline-none' /></label>
						<div className='grid grid-cols-3 gap-3 max-sm:gap-2'><label className='col-span-2'><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Número do conselho</span><input type='text' placeholder='0000000' value={professional.numeroConselho} onChange={(event) => setField('numeroConselho', event.target.value)} inputMode='numeric' maxLength={20} className='h-[34px] w-full rounded-md border border-gray-200 px-3 text-[13px] text-gray-600 outline-none' /></label><label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>UF</span><select value={professional.ufConselho} onChange={(event) => setField('ufConselho', event.target.value)} className='h-[34px] w-full rounded-md border border-gray-200 bg-white px-3 text-[13px] text-gray-600 outline-none'>{UF_OPTIONS.map((uf) => <option key={uf} value={uf}>{uf}</option>)}</select></label></div>
						<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Categoria</span><select value={professional.categoria} onChange={(event) => setField('categoria', event.target.value)} className={`h-[34px] w-full rounded-md border bg-white px-3 text-[13px] text-gray-600 outline-none ${fieldBorder('categoria')}`}><option value=''>Selecione a categoria</option>{categories.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
						<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Telefone</span><input type='text' placeholder='(00) 0 0000-0000' value={professional.telefone} onChange={(event) => setField('telefone', formatPhone(event.target.value))} inputMode='numeric' maxLength={15} className='h-[34px] w-full rounded-md border border-gray-200 px-3 text-[13px] text-gray-600 outline-none' /></label>

						{showSchedule && <fieldset disabled={isScheduleReadOnly} className={`lg:col-span-2 ${isScheduleReadOnly ? 'opacity-75' : ''}`}>
							{isScheduleReadOnly && <p className='mb-3 rounded-md border border-[#AFC4D8] bg-[#F8FBFE] px-3 py-2 text-[11px] text-[#54708A]'>Esta jornada pertence ao histórico mantido do profissional e não possui cards futuros. As informações abaixo são somente para visualização.</p>}
							<div className='grid grid-cols-1 gap-3 lg:grid-cols-3 max-sm:gap-2'>
								<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Regime</span><select value={professional.regime} onChange={(event) => setField('regime', event.target.value)} className={`h-[34px] w-full rounded-md border px-3 text-[13px] text-gray-600 outline-none ${isScheduleReadOnly ? 'cursor-not-allowed bg-[#F3F4F6] border-gray-200' : `bg-white ${fieldBorder('regime')}`}`}>{regimes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
								<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Recorrência</span><select value={professional.recorrencia} onChange={(event) => setField('recorrencia', event.target.value)} className={`h-[34px] w-full rounded-md border px-3 text-[13px] text-gray-600 outline-none ${isScheduleReadOnly ? 'cursor-not-allowed bg-[#F3F4F6] border-gray-200' : `bg-white ${fieldBorder('recorrencia')}`}`}>{visibleRecurrences.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
				<label><span className='mb-1 block text-[13px] font-medium text-[#13335A]'>Data inicial</span><input type='date' value={professional.vigenciaInicio} min={isEditing ? undefined : getTodayIsoDate()} onChange={(event) => setField('vigenciaInicio', event.target.value)} className={`h-[34px] w-full rounded-md border px-3 text-[13px] text-gray-600 outline-none ${isScheduleReadOnly ? 'cursor-not-allowed bg-[#F3F4F6] border-gray-200' : `bg-white ${fieldBorder('vigenciaInicio')}`}`} /></label>
							</div>

			{hasSchedule && isWeekly && <div className={`mt-3 rounded-md border px-2 py-1 pb-2 shadow-md ${fieldErrors.diasSemana || fieldErrors.horarios ? 'border-[#D92D20] ring-1 ring-[#FECACA]' : 'border-gray-200'}`}><label className='mb-1 block text-[11px] font-medium text-[#13335A]'>Dias recorrentes</label><div className='flex w-full flex-wrap justify-start gap-2'>{weekDays.map((day) => { const selected = professional.diasSemana.includes(day.value); const schedule = professional.diasSemanaHorarios?.[day.value] || { horaInicio: professional.horaInicio, horaFim: professional.horaFim }; return <div key={day.value} className='flex w-[116px] max-w-full shrink-0 flex-col gap-2'><button type='button' onClick={() => toggleWeekDay(day.value)} className={`h-[34px] w-full rounded-md border px-1 text-[13px] font-medium transition-all duration-200 ${selected ? 'border-[#004A80] bg-[#004A80] text-white' : fieldErrors.diasSemana ? 'border-[#D92D20] bg-white text-[#6B7280]' : 'border-gray-200 bg-white text-[#6B7280] hover:bg-[#F3F4F6]'}`}>{WEEKDAY_LABELS[day.value] || day.label}</button><HourRangeControl minWidthClassName='w-full min-w-0' marginTopClassName='mt-0' disabled={!selected} hasError={selected && Boolean(fieldErrors.horarios)} horaInicio={schedule.horaInicio} horaFim={schedule.horaFim} onChangeHoraInicio={(value) => updateWeekDayTime(day.value, 'horaInicio', value)} onChangeHoraFim={(value) => updateWeekDayTime(day.value, 'horaFim', value)} /></div>; })}</div></div>}
							{hasSchedule && isRotation && <div className='mt-3 rounded-md border border-[#B8C7D9] px-3 py-3 shadow-md'>{professional.semanasRotacao.map((week, index) => <div key={index} className='mb-3 last:mb-0'><p className='mb-1 text-[12px] font-medium text-[#13335A]'>Semana {index + 1}</p><div className='flex flex-wrap gap-1'>{weekDays.map((day) => <button type='button' key={day.value} onClick={() => onToggleRotationWeekDay(index, day.value)} className={`w-20 rounded-md border px-2 py-1 text-center text-[12px] ${week.includes(day.value) ? 'border-[#004A80] bg-[#004A80] text-white' : 'border-gray-200 bg-white text-[#36516b]'}`}>{WEEKDAY_LABELS[day.value] || day.label}</button>)}</div></div>)}</div>}
							{hasSchedule && !isWeekly && !isRotation && !isSpecificDates && <div className={`mt-3 rounded-md border px-3 py-2 shadow-md ${fieldErrors.horarios ? 'border-[#D92D20] ring-1 ring-[#FECACA]' : 'border-gray-200'}`}><label className='mb-2 block text-[13px] font-medium text-[#13335A]'>Horário</label><div className='w-full max-w-[136px]'><HourRangeControl hasError={Boolean(fieldErrors.horarios)} horaInicio={professional.horaInicio} horaFim={professional.horaFim} onChangeHoraInicio={(value) => setField('horaInicio', value)} onChangeHoraFim={(value) => setField('horaFim', value)} /></div></div>}
							{professional.datasEspecificas.length > 0 ? (
								<div className='mt-4 overflow-x-hidden rounded-md border px-2 py-1 shadow-md max-sm:mt-3'>
									<p className='block text-[13px] font-medium text-[#13335A]'>
										Datas específicas
									</p>
									<div className='flex flex-wrap gap-3 py-2 max-sm:justify-start max-sm:gap-2'>
										{professional.datasEspecificas.map((date, index) => {
											const hours = professional.datasEspecificasHorarios?.[index] || {
												horaInicio: professional.horaInicio,
												horaFim: professional.horaFim,
											};
											return (
												<div key={`${date}-${index}`} className='flex flex-col items-center gap-1 max-sm:gap-1'>
													<div className='relative rounded-md border border-gray-200 bg-white p-1'>
														<span className='mb-1 block text-center text-[9px] font-semibold uppercase text-[#6B7280]'>
															Alocação
														</span>
														<button
															type='button'
															aria-label='Remover data específica'
															onClick={() => onRemoveSpecificDate(index)}
															className='absolute right-1 top-1 flex h-[14px] w-[14px] items-center justify-center rounded-[3px] bg-gray-100 text-[17px] font-normal leading-none text-[#BD504E] transition hover:bg-gray-200 hover:text-[#883433]'>
															&times;
														</button>
														<div className='flex max-w-[136px] flex-col gap-1'>
															<input
																type='date'
																aria-label='Data específica'
										value={date}
										min={isEditing ? undefined : getTodayIsoDate()}
																onChange={(event) => onUpdateSpecificDate(index, event.target.value)}
																className={`h-[34px] w-full rounded-md border px-1 text-[12px] text-[#4F5257] outline-none ${fieldBorder('datasEspecificas')}`}
															/>
															<HourRangeControl
																minWidthClassName='w-full'
																marginTopClassName='mt-0'
																containerClassName='rounded-md'
																horaInicio={hours.horaInicio}
																horaFim={hours.horaFim}
																hasError={Boolean(fieldErrors.horarios)}
																onChangeHoraInicio={(value) => updateSpecificHours(index, 'horaInicio', value)}
																onChangeHoraFim={(value) => updateSpecificHours(index, 'horaFim', value)}
															/>
														</div>
													</div>
												</div>
											);
										})}
										<button
											type='button'
											aria-label='Adicionar data específica'
											onClick={addSpecificDate}
											className='inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md border border-gray-300 text-[18px] font-medium leading-none text-[#13335A] transition hover:bg-[#F8FAFC]'>
											+
										</button>
									</div>
								</div>
							) : (
								<div className='mt-4 flex items-center gap-2'>
									<button
										type='button'
										onClick={addSpecificDate}
										className='inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-[12px] font-medium leading-none text-[#13335A] transition hover:bg-[#F8FAFC]'>
										+ Data específica
									</button>
								</div>
							)}
						</fieldset>}
					</div>
					</fieldset>

					{error && <p className='mt-4 text-center text-[13px] text-[#B42318]'>{error}</p>}
					<div className='mt-5 flex justify-center'><button type={isReadOnly ? 'button' : 'submit'} onClick={isReadOnly ? onClose : undefined} disabled={submitting} className='h-[30px] w-full max-w-[170px] rounded-md border border-gray-200 text-[12px] text-[#7b7d80] shadow-sm transition hover:bg-gray-50 disabled:opacity-50'>{isReadOnly ? 'Fechar' : submitting ? 'Enviando...' : 'Enviar'}</button></div>
				</div>
			</form>
		</div>
	);
}

export default NovoProfissionalMultiprofissionalModal;
